import { QuestionType, StudentGender, StudentGenderToLabel, ActualValues } from '@mightybyte/business';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CustomButton } from '../uiWrappers/button';
import { useMemo } from 'react';

export type { ActualValues };

interface ActualValuesInputProps {
  questionType: QuestionType;
  value: ActualValues | null;
  onChange: (value: ActualValues | null) => void;
}

export function ActualValuesInput({ questionType, value, onChange }: ActualValuesInputProps) {
  // Don't show for SingleChoice
  if (questionType === QuestionType.SingleChoice) {
    return null;
  }

  const genders = [StudentGender.son, StudentGender.daughter, StudentGender.preferNotToAnswer];

  const handleNumericChange = (gender: StudentGender, newValue: string) => {
    if (newValue === '' || newValue === null || newValue === undefined) {
      // If empty, remove this gender from values
      if (value?.type === QuestionType.Numeric) {
        const { [gender]: _, ...rest } = value.values;
        const hasRemainingValues = Object.keys(rest).length > 0;
        onChange(hasRemainingValues ? { type: QuestionType.Numeric, values: rest as any } : null);
      }
      return;
    }

    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      onChange({
        type: QuestionType.Numeric,
        values: {
          ...(value?.type === QuestionType.Numeric ? value.values : {}),
          [gender]: numValue,
        },
      });
    }
  };

  const handlePercentageChange = (gender: StudentGender, newValue: string) => {
    if (newValue === '' || newValue === null || newValue === undefined) {
      // If empty, remove this gender from values
      if (value?.type === QuestionType.Percentage) {
        const { [gender]: _, ...rest } = value.values;
        const hasRemainingValues = Object.keys(rest).length > 0;
        onChange(hasRemainingValues ? { type: QuestionType.Percentage, values: rest as any } : null);
      }
      return;
    }

    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      onChange({
        type: QuestionType.Percentage,
        values: {
          ...(value?.type === QuestionType.Percentage ? value.values : {}),
          [gender]: numValue,
        },
      });
    }
  };

  const handleBooleanChange = (gender: StudentGender, newValue: string) => {
    const boolValue = newValue === 'true';
    onChange({
      type: QuestionType.Boolean,
      values: {
        ...(value?.type === QuestionType.Boolean ? value.values : {}),
        [gender]: boolValue,
      },
    });
  };

  const subtitleValueDescription = useMemo(()=>{
    switch (questionType) {
        case QuestionType.Boolean: {
            return '(Majority)';
        }
        case QuestionType.Percentage: {
          return '(%)'
        }
      default: {
          return ''
        }
    }
  },[questionType])

  return (
    <div className="space-y-3 pb-4 border-b">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-foreground tracking-wide">
          Actual Values
          <span className={'ml-2 font-medium text-muted-foreground text-sm'}>{`${subtitleValueDescription}`}</span>
        </h3>
      </div>

      {questionType === QuestionType.Numeric && (
        <div className="grid grid-cols-3 gap-3">
          {genders.map((gender) => (
            <div key={gender}>
              <Label htmlFor={`actual-${gender}`}>{StudentGenderToLabel[gender]}</Label>
              <Input
                id={`actual-${gender}`}
                type="number"
                min="0"
                step="any"
                value={value?.type === QuestionType.Numeric ? (value.values[gender] ?? '') : ''}
                onChange={(e) => handleNumericChange(gender, e.target.value)}
                className="h-9"
              />
            </div>
          ))}
        </div>
      )}

      {questionType === QuestionType.Percentage && (
        <div className="grid grid-cols-3 gap-3">
          {genders.map((gender) => (
            <div key={gender}>
              <Label htmlFor={`actual-${gender}`}>{StudentGenderToLabel[gender]}</Label>
              <Input
                id={`actual-${gender}`}
                type="number"
                min="0"
                max="100"
                value={value?.type === QuestionType.Percentage ? (value.values[gender] ?? '') : ''}
                onChange={(e) => handlePercentageChange(gender, e.target.value)}
                placeholder="0-100"
                className="h-9"
              />
            </div>
          ))}
        </div>
      )}

      {questionType === QuestionType.Boolean && (
        <div className="grid grid-cols-3 gap-3">
          {genders.map((gender) => (
            <div key={gender}>
              <Label htmlFor={`actual-${gender}`}>{StudentGenderToLabel[gender]}</Label>
              <Select
                value={value?.type === QuestionType.Boolean && value.values[gender] !== undefined ? String(value.values[gender]) : undefined}
                onValueChange={(val) => handleBooleanChange(gender, val)}
              >
                <SelectTrigger className="h-9 min-w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">True/Yes</SelectItem>
                  <SelectItem value="false">False/No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
